/**
 * Volume ops shared by the ONNX models (SynthStrip, DAGMNet DWI/PWI).
 *
 * Index conventions:
 *   F-order (ours, and NIfTI's on disk):  idx = x + y*nx + z*nx*ny
 *   C-order (what ONNX/PyTorch expect):   idx = (x*ny + y)*nz + z
 *
 * Ported from openads-web/lib/wasm/volume.ts (same project, MIT-equivalent
 * internal code) and extended with the connected-component "remove small
 * objects" variant DAGMNet's postprocess needs (openads-web's SynthStrip only
 * needed "keep largest + fill").
 */

export type Dims = [number, number, number];

export const voxelCount = (d: Dims) => d[0] * d[1] * d[2];

export function fToC(data: Float32Array, dims: Dims): Float32Array {
  const [nx, ny, nz] = dims;
  const out = new Float32Array(data.length);
  for (let z = 0; z < nz; z++) {
    for (let y = 0; y < ny; y++) {
      for (let x = 0; x < nx; x++) {
        out[(x * ny + y) * nz + z] = data[x + y * nx + z * nx * ny];
      }
    }
  }
  return out;
}

export function cToF(data: Float32Array, dims: Dims): Float32Array {
  const [nx, ny, nz] = dims;
  const out = new Float32Array(data.length);
  for (let z = 0; z < nz; z++) {
    for (let y = 0; y < ny; y++) {
      for (let x = 0; x < nx; x++) {
        out[x + y * nx + z * nx * ny] = data[(x * ny + y) * nz + z];
      }
    }
  }
  return out;
}

export type AxisCode = "R" | "L" | "A" | "P" | "S" | "I";
export type Orientation = [AxisCode, AxisCode, AxisCode];

const OPPOSITE: Record<AxisCode, AxisCode> = { R: "L", L: "R", A: "P", P: "A", S: "I", I: "S" };
const WORLD_AXIS: Record<AxisCode, number> = { L: 0, R: 0, P: 1, A: 1, I: 2, S: 2 };
const POSITIVE: AxisCode[] = ["R", "A", "S"];

export function orientationFromAffine(affine: Float32Array): Orientation {
  const codes: AxisCode[] = [];
  const used = new Set<number>();
  for (let col = 0; col < 3; col++) {
    let bestAxis = 0;
    let bestMag = -1;
    for (let row = 0; row < 3; row++) {
      const mag = Math.abs(affine[row * 4 + col]);
      if (mag > bestMag && !used.has(row)) {
        bestMag = mag;
        bestAxis = row;
      }
    }
    used.add(bestAxis);
    const positive = affine[bestAxis * 4 + col] >= 0;
    const code = POSITIVE[bestAxis];
    codes.push(positive ? code : OPPOSITE[code]);
  }
  return codes as Orientation;
}

export function axisPermutation(from: Orientation, to: Orientation): { srcAxis: Dims; flip: boolean[] } {
  const srcAxis: number[] = [];
  const flip: boolean[] = [];
  for (let d = 0; d < 3; d++) {
    const want = to[d];
    const s = from.findIndex((c) => WORLD_AXIS[c] === WORLD_AXIS[want]);
    if (s < 0) throw new Error(`cannot map orientation ${from.join("")} -> ${to.join("")}`);
    srcAxis.push(s);
    flip.push(from[s] !== want);
  }
  return { srcAxis: srcAxis as Dims, flip };
}

export function permuteAxes(values: Dims, from: Orientation, to: Orientation): Dims {
  const { srcAxis } = axisPermutation(from, to);
  return [values[srcAxis[0]], values[srcAxis[1]], values[srcAxis[2]]];
}

export function reorient(
  data: Float32Array,
  dims: Dims,
  from: Orientation,
  to: Orientation
): { data: Float32Array; dims: Dims } {
  const { srcAxis, flip } = axisPermutation(from, to);
  const outDims = [dims[srcAxis[0]], dims[srcAxis[1]], dims[srcAxis[2]]] as Dims;
  const out = new Float32Array(voxelCount(outDims));
  const srcStride = [1, dims[0], dims[0] * dims[1]];
  const coord = [0, 0, 0];

  for (let z = 0; z < outDims[2]; z++) {
    for (let y = 0; y < outDims[1]; y++) {
      for (let x = 0; x < outDims[0]; x++) {
        const dst = [x, y, z];
        let si = 0;
        for (let d = 0; d < 3; d++) {
          const s = srcAxis[d];
          coord[s] = flip[d] ? dims[s] - 1 - dst[d] : dst[d];
          si += coord[s] * srcStride[s];
        }
        out[x + y * outDims[0] + z * outDims[0] * outDims[1]] = data[si];
      }
    }
  }
  return { data: out, dims: outDims };
}

export function reorientMask(
  data: Uint8Array,
  dims: Dims,
  from: Orientation,
  to: Orientation
): { data: Uint8Array; dims: Dims } {
  const f = new Float32Array(data.length);
  for (let i = 0; i < data.length; i++) f[i] = data[i];
  const r = reorient(f, dims, from, to);
  const out = new Uint8Array(r.data.length);
  for (let i = 0; i < r.data.length; i++) out[i] = r.data[i] > 0.5 ? 1 : 0;
  return { data: out, dims: r.dims };
}

export function resampleToVoxelSize(
  data: Float32Array,
  dims: Dims,
  pixdim: Dims,
  target: Dims
): { data: Float32Array; dims: Dims } {
  const scale = [pixdim[0] / target[0], pixdim[1] / target[1], pixdim[2] / target[2]];
  const outDims = [
    Math.max(1, Math.round(dims[0] * scale[0])),
    Math.max(1, Math.round(dims[1] * scale[1])),
    Math.max(1, Math.round(dims[2] * scale[2])),
  ] as Dims;
  const out = new Float32Array(voxelCount(outDims));
  const [nx, ny, nz] = dims;

  const at = (x: number, y: number, z: number) =>
    x < 0 || y < 0 || z < 0 || x >= nx || y >= ny || z >= nz ? 0 : data[x + y * nx + z * nx * ny];

  for (let z = 0; z < outDims[2]; z++) {
    const sz = z / scale[2];
    const z0 = Math.floor(sz);
    const wz = sz - z0;
    for (let y = 0; y < outDims[1]; y++) {
      const sy = y / scale[1];
      const y0 = Math.floor(sy);
      const wy = sy - y0;
      for (let x = 0; x < outDims[0]; x++) {
        const sx = x / scale[0];
        const x0 = Math.floor(sx);
        const wx = sx - x0;
        const c00 = at(x0, y0, z0) * (1 - wx) + at(x0 + 1, y0, z0) * wx;
        const c10 = at(x0, y0 + 1, z0) * (1 - wx) + at(x0 + 1, y0 + 1, z0) * wx;
        const c01 = at(x0, y0, z0 + 1) * (1 - wx) + at(x0 + 1, y0, z0 + 1) * wx;
        const c11 = at(x0, y0 + 1, z0 + 1) * (1 - wx) + at(x0 + 1, y0 + 1, z0 + 1) * wx;
        const c0 = c00 * (1 - wy) + c10 * wy;
        const c1 = c01 * (1 - wy) + c11 * wy;
        out[x + y * outDims[0] + z * outDims[0] * outDims[1]] = c0 * (1 - wz) + c1 * wz;
      }
    }
  }
  return { data: out, dims: outDims };
}

export interface BBox {
  min: Dims;
  max: Dims;
}

export function boundingBox(data: Float32Array, dims: Dims, threshold = 0): BBox | null {
  const [nx, ny, nz] = dims;
  const min: Dims = [nx, ny, nz];
  const max: Dims = [-1, -1, -1];
  for (let z = 0; z < nz; z++) {
    for (let y = 0; y < ny; y++) {
      for (let x = 0; x < nx; x++) {
        if (data[x + y * nx + z * nx * ny] > threshold) {
          if (x < min[0]) min[0] = x;
          if (y < min[1]) min[1] = y;
          if (z < min[2]) min[2] = z;
          if (x > max[0]) max[0] = x;
          if (y > max[1]) max[1] = y;
          if (z > max[2]) max[2] = z;
        }
      }
    }
  }
  return max[0] < 0 ? null : { min, max };
}

export function cropToBBox(data: Float32Array, dims: Dims, bbox: BBox): { data: Float32Array; dims: Dims } {
  const [nx, ny] = dims;
  const cd: Dims = [bbox.max[0] - bbox.min[0] + 1, bbox.max[1] - bbox.min[1] + 1, bbox.max[2] - bbox.min[2] + 1];
  const out = new Float32Array(voxelCount(cd));
  for (let z = 0; z < cd[2]; z++) {
    for (let y = 0; y < cd[1]; y++) {
      for (let x = 0; x < cd[0]; x++) {
        out[x + y * cd[0] + z * cd[0] * cd[1]] =
          data[(x + bbox.min[0]) + (y + bbox.min[1]) * nx + (z + bbox.min[2]) * nx * ny];
      }
    }
  }
  return { data: out, dims: cd };
}

/** SynthStrip's conformed shape: np.clip(np.ceil(shape / 64) * 64, 192, 320) */
export function conformedTargetDims(cropDims: Dims): Dims {
  return cropDims.map((s) => Math.min(320, Math.max(192, Math.ceil(s / 64) * 64))) as Dims;
}

export function centerPad(data: Float32Array, dims: Dims, target: Dims): { data: Float32Array; offsets: Dims } {
  const out = new Float32Array(voxelCount(target));
  const offsets = [0, 1, 2].map((i) => Math.floor((target[i] - dims[i]) / 2)) as Dims;
  for (let z = 0; z < dims[2]; z++) {
    const tz = z + offsets[2];
    if (tz < 0 || tz >= target[2]) continue;
    for (let y = 0; y < dims[1]; y++) {
      const ty = y + offsets[1];
      if (ty < 0 || ty >= target[1]) continue;
      for (let x = 0; x < dims[0]; x++) {
        const tx = x + offsets[0];
        if (tx < 0 || tx >= target[0]) continue;
        out[tx + ty * target[0] + tz * target[0] * target[1]] = data[x + y * dims[0] + z * dims[0] * dims[1]];
      }
    }
  }
  return { data: out, offsets };
}

export function uncenterPad<T extends Float32Array | Uint8Array>(
  padded: T,
  target: Dims,
  dims: Dims,
  offsets: Dims,
  make: (n: number) => T
): T {
  const out = make(voxelCount(dims));
  for (let z = 0; z < dims[2]; z++) {
    const sz = z + offsets[2];
    if (sz < 0 || sz >= target[2]) continue;
    for (let y = 0; y < dims[1]; y++) {
      const sy = y + offsets[1];
      if (sy < 0 || sy >= target[1]) continue;
      for (let x = 0; x < dims[0]; x++) {
        const sx = x + offsets[0];
        if (sx < 0 || sx >= target[0]) continue;
        out[x + y * dims[0] + z * dims[0] * dims[1]] = padded[sx + sy * target[0] + sz * target[0] * target[1]];
      }
    }
  }
  return out;
}

export function p99Normalize(data: Float32Array): { data: Float32Array; min: number; p99: number } {
  const n = data.length;
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < n; i++) {
    const v = data[i];
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (!isFinite(min)) min = 0;
  if (!isFinite(max)) max = 0;

  const range = max - min;
  const BINS = 65536;
  let p99 = 0;
  if (range > 0) {
    const hist = new Uint32Array(BINS);
    const scale = (BINS - 1) / range;
    for (let i = 0; i < n; i++) hist[(((data[i] - min) * scale) | 0)]++;
    const cutoff = n * 0.99;
    let cum = 0;
    for (let b = 0; b < BINS; b++) {
      cum += hist[b];
      if (cum >= cutoff) {
        p99 = b / scale;
        break;
      }
    }
  }

  const denom = p99 || 1;
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const v = (data[i] - min) / denom;
    out[i] = v < 0 ? 0 : v > 1 ? 1 : v;
  }
  return { data: out, min, p99 };
}

/**
 * DAGMNet's `zscore_in_mask`: mean/std over the masked voxels only (or the
 * whole array if fewer than 10 voxels are masked in). `mask` here is whatever
 * the caller already computed (DWI and PWI build it differently — see the
 * callers), matching passing an explicit `mask=` to the Python function.
 */
export function zscoreInMask(data: Float32Array, mask: Uint8Array, eps = 1e-6): Float32Array {
  let count = 0;
  let sum = 0;
  for (let i = 0; i < data.length; i++) if (mask[i]) { sum += data[i]; count++; }
  let mean: number;
  let std: number;
  if (count >= 10) {
    mean = sum / count;
    let ss = 0;
    for (let i = 0; i < data.length; i++) if (mask[i]) { const d = data[i] - mean; ss += d * d; }
    std = Math.sqrt(ss / count);
  } else {
    let s = 0;
    for (let i = 0; i < data.length; i++) s += data[i];
    mean = s / data.length;
    let ss = 0;
    for (let i = 0; i < data.length; i++) { const d = data[i] - mean; ss += d * d; }
    std = Math.sqrt(ss / data.length);
  }
  const denom = std > eps ? std : eps;
  const out = new Float32Array(data.length);
  for (let i = 0; i < data.length; i++) out[i] = (data[i] - mean) / denom;
  return out;
}

/** numpy-compatible percentile (default 'linear' interpolation method) on an already-sorted array. */
function percentileOfSorted(sorted: Float32Array, p: number): number {
  const n = sorted.length;
  if (n === 0) return 0;
  if (n === 1) return sorted[0];
  const idx = (p / 100) * (n - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}

/**
 * Real OpenADS's `get_dwi_normalized` (core/preprocessing.py): clip masked
 * voxels to [1st, 99th] percentile (computed over the non-negative subset of
 * in-mask voxels, matching `values = values[values >= 0]` before computing
 * percentiles — the clip itself is still applied to all in-mask voxels,
 * negative or not), then rescale that clipped range to [0, 1]. Out-of-mask
 * voxels are 0.
 *
 * This implements only the percentile fallback path — the real function
 * first attempts a bimodal-KDE peak-finding bound estimate
 * (`scipy.stats.gaussian_kde` + `scipy.signal.find_peaks`) and only falls
 * back to plain percentiles when that fails or isn't attempted (< 100
 * values, or fewer than 2 peaks found). Not replicated here: KDE-based
 * bimodal peak-finding is a large, fragile port for a path the real code's
 * own try/except treats as best-effort, not load-bearing.
 */
export function percentileClipRescaleInMask(
  data: Float32Array,
  mask: Uint8Array,
  pLow = 1,
  pHigh = 99
): Float32Array {
  let nonNegCount = 0;
  for (let i = 0; i < data.length; i++) if (mask[i] && data[i] >= 0) nonNegCount++;
  const sorted = new Float32Array(nonNegCount);
  let k = 0;
  for (let i = 0; i < data.length; i++) if (mask[i] && data[i] >= 0) sorted[k++] = data[i];
  sorted.sort();

  const lower = percentileOfSorted(sorted, pLow);
  const upper = percentileOfSorted(sorted, pHigh);
  const range = upper - lower;

  const out = new Float32Array(data.length);
  for (let i = 0; i < data.length; i++) {
    if (!mask[i]) continue; // stays 0, matches np.zeros_like(dwi_img) base
    let v = data[i];
    v = v < lower ? lower : v > upper ? upper : v;
    out[i] = range > 0 ? (v - lower) / range : 0;
  }
  return out;
}

export function zscoreNormalize(data: Float32Array): Float32Array {
  const n = data.length;
  let sum = 0;
  for (let i = 0; i < n; i++) sum += data[i];
  const mean = sum / n;
  let ss = 0;
  for (let i = 0; i < n; i++) {
    const d = data[i] - mean;
    ss += d * d;
  }
  const std = Math.sqrt(ss / n) || 1;
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) out[i] = (data[i] - mean) / std;
  return out;
}

/** Keeps the largest 6-connected component and fills interior holes (SynthStrip postprocess). */
export function keepLargestComponentAndFill(mask: Uint8Array, dims: Dims): Uint8Array {
  const [nx, ny, nz] = dims;
  const n = nx * ny * nz;
  const idx = (x: number, y: number, z: number) => x + y * nx + z * nx * ny;

  const label = new Int32Array(n).fill(-1);
  let best = -1;
  let bestSize = 0;
  let current = 0;
  const stack = new Int32Array(n);

  for (let s = 0; s < n; s++) {
    if (mask[s] === 0 || label[s] !== -1) continue;
    let top = 0;
    stack[top++] = s;
    label[s] = current;
    let size = 0;
    while (top > 0) {
      const p = stack[--top];
      size++;
      const x = p % nx;
      const y = ((p / nx) | 0) % ny;
      const z = (p / (nx * ny)) | 0;
      if (x > 0) { const q = p - 1; if (mask[q] && label[q] === -1) { label[q] = current; stack[top++] = q; } }
      if (x < nx - 1) { const q = p + 1; if (mask[q] && label[q] === -1) { label[q] = current; stack[top++] = q; } }
      if (y > 0) { const q = p - nx; if (mask[q] && label[q] === -1) { label[q] = current; stack[top++] = q; } }
      if (y < ny - 1) { const q = p + nx; if (mask[q] && label[q] === -1) { label[q] = current; stack[top++] = q; } }
      if (z > 0) { const q = p - nx * ny; if (mask[q] && label[q] === -1) { label[q] = current; stack[top++] = q; } }
      if (z < nz - 1) { const q = p + nx * ny; if (mask[q] && label[q] === -1) { label[q] = current; stack[top++] = q; } }
    }
    if (size > bestSize) { bestSize = size; best = current; }
    current++;
  }

  const out = new Uint8Array(n);
  if (best < 0) return out;
  for (let i = 0; i < n; i++) out[i] = label[i] === best ? 1 : 0;

  const outside = new Uint8Array(n);
  let top = 0;
  const push = (p: number) => { if (!out[p] && !outside[p]) { outside[p] = 1; stack[top++] = p; } };
  for (let z = 0; z < nz; z++) {
    for (let y = 0; y < ny; y++) {
      for (let x = 0; x < nx; x++) {
        if (x === 0 || y === 0 || z === 0 || x === nx - 1 || y === ny - 1 || z === nz - 1) push(idx(x, y, z));
      }
    }
  }
  while (top > 0) {
    const p = stack[--top];
    const x = p % nx;
    const y = ((p / nx) | 0) % ny;
    const z = (p / (nx * ny)) | 0;
    if (x > 0) push(p - 1);
    if (x < nx - 1) push(p + 1);
    if (y > 0) push(p - nx);
    if (y < ny - 1) push(p + nx);
    if (z > 0) push(p - nx * ny);
    if (z < nz - 1) push(p + nx * ny);
  }
  for (let i = 0; i < n; i++) if (!outside[i]) out[i] = 1;
  return out;
}

/** scipy.ndimage.label + remove components smaller than min_size (DAGMNet's `remove_small_objects_3d`). 6-connectivity. */
export function removeSmallObjects(mask: Uint8Array, dims: Dims, minSize: number): Uint8Array {
  const [nx, ny, nz] = dims;
  const n = nx * ny * nz;
  const label = new Int32Array(n).fill(-1);
  const stack = new Int32Array(n);
  const sizes: number[] = [];
  let current = 0;

  for (let s = 0; s < n; s++) {
    if (mask[s] === 0 || label[s] !== -1) continue;
    let top = 0;
    stack[top++] = s;
    label[s] = current;
    let size = 0;
    while (top > 0) {
      const p = stack[--top];
      size++;
      const x = p % nx;
      const y = ((p / nx) | 0) % ny;
      const z = (p / (nx * ny)) | 0;
      if (x > 0) { const q = p - 1; if (mask[q] && label[q] === -1) { label[q] = current; stack[top++] = q; } }
      if (x < nx - 1) { const q = p + 1; if (mask[q] && label[q] === -1) { label[q] = current; stack[top++] = q; } }
      if (y > 0) { const q = p - nx; if (mask[q] && label[q] === -1) { label[q] = current; stack[top++] = q; } }
      if (y < ny - 1) { const q = p + nx; if (mask[q] && label[q] === -1) { label[q] = current; stack[top++] = q; } }
      if (z > 0) { const q = p - nx * ny; if (mask[q] && label[q] === -1) { label[q] = current; stack[top++] = q; } }
      if (z < nz - 1) { const q = p + nx * ny; if (mask[q] && label[q] === -1) { label[q] = current; stack[top++] = q; } }
    }
    sizes[current] = size;
    current++;
  }

  const out = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const l = label[i];
    out[i] = l >= 0 && sizes[l] >= minSize ? 1 : 0;
  }
  return out;
}

/** scipy.ndimage.binary_closing(structure=np.ones((2,2,2))): dilation then erosion with a 2x2x2 all-ones structuring element. */
export function binaryClosing2x2x2(mask: Uint8Array, dims: Dims): Uint8Array {
  const [nx, ny, nz] = dims;
  const at = (a: Uint8Array, x: number, y: number, z: number) =>
    x < 0 || y < 0 || z < 0 || x >= nx || y >= ny || z >= nz ? 0 : a[x + y * nx + z * nx * ny];

  // scipy centers a 2x2x2 structure with origin at floor(size/2)=1 in each
  // axis, i.e. the neighbourhood for output voxel p is offsets in {-1,0}.
  const dilate = (a: Uint8Array): Uint8Array => {
    const out = new Uint8Array(nx * ny * nz);
    for (let z = 0; z < nz; z++)
      for (let y = 0; y < ny; y++)
        for (let x = 0; x < nx; x++) {
          let v = 0;
          for (let dz = -1; dz <= 0 && !v; dz++)
            for (let dy = -1; dy <= 0 && !v; dy++)
              for (let dx = -1; dx <= 0 && !v; dx++)
                if (at(a, x + dx, y + dy, z + dz)) v = 1;
          out[x + y * nx + z * nx * ny] = v;
        }
    return out;
  };
  const erode = (a: Uint8Array): Uint8Array => {
    const out = new Uint8Array(nx * ny * nz);
    for (let z = 0; z < nz; z++)
      for (let y = 0; y < ny; y++)
        for (let x = 0; x < nx; x++) {
          let v = 1;
          for (let dz = 0; dz <= 1 && v; dz++)
            for (let dy = 0; dy <= 1 && v; dy++)
              for (let dx = 0; dx <= 1 && v; dx++)
                if (!at(a, x + dx, y + dy, z + dz)) v = 0;
          out[x + y * nx + z * nx * ny] = v;
        }
    return out;
  };
  return erode(dilate(mask));
}

/** scipy.ndimage.binary_fill_holes: flood background from the border; unreached background is interior and gets filled. */
export function binaryFillHoles(mask: Uint8Array, dims: Dims): Uint8Array {
  const [nx, ny, nz] = dims;
  const n = nx * ny * nz;
  const idx = (x: number, y: number, z: number) => x + y * nx + z * nx * ny;
  const outside = new Uint8Array(n);
  const stack = new Int32Array(n);
  let top = 0;
  const push = (p: number) => { if (!mask[p] && !outside[p]) { outside[p] = 1; stack[top++] = p; } };
  for (let z = 0; z < nz; z++)
    for (let y = 0; y < ny; y++)
      for (let x = 0; x < nx; x++)
        if (x === 0 || y === 0 || z === 0 || x === nx - 1 || y === ny - 1 || z === nz - 1) push(idx(x, y, z));
  while (top > 0) {
    const p = stack[--top];
    const x = p % nx;
    const y = ((p / nx) | 0) % ny;
    const z = (p / (nx * ny)) | 0;
    if (x > 0) push(p - 1);
    if (x < nx - 1) push(p + 1);
    if (y > 0) push(p - nx);
    if (y < ny - 1) push(p + nx);
    if (z > 0) push(p - nx * ny);
    if (z < nz - 1) push(p + nx * ny);
  }
  const out = new Uint8Array(n);
  for (let i = 0; i < n; i++) out[i] = mask[i] || !outside[i] ? 1 : 0;
  return out;
}

/** DAGMNet postprocess: binary_closing(2x2x2) -> fill_holes -> remove_small_objects(min_size). */
export function dagmnetPostprocess(mask: Uint8Array, dims: Dims, minSize: number): Uint8Array {
  const closed = binaryClosing2x2x2(mask, dims);
  const filled = binaryFillHoles(closed, dims);
  return removeSmallObjects(filled, dims, minSize);
}
